<template>
  <layout-page-head-section>
    <div v-auto-animate="{duration: 200}">
      <div v-if="condensed" class="flex min-w-0 items-center gap-3">
        <user-avatar :user="user" size="sm" />
        <div class="flex min-w-0 flex-1 items-center gap-1">
          <p class="min-w-0 truncate font-serif text-2xl">{{ user.name }}</p>
          <u-button
            :icon="COMMON_ICONS.information"
            color="gray"
            variant="ghost"
            title="More Information"
            class="group mt-1 shrink-0 rounded-full"
            :ui="informationButtonUi"
            @click="openDetailsModal({user})"
          />
        </div>
        <user-profile-actions
          compact
          :user="user"
          :is-current-user="isCurrentUser"
          :privacy="privacy"
          @update:privacy="$emit('update:privacy', $event)"
        />
      </div>

      <div v-else class="profile-header__expanded overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="profile-header__banner relative h-48 overflow-hidden bg-slate-700 sm:h-64 xl:h-72">
          <core-image-banner-media
            :src="bannerSource"
            :alt="hasBanner ? `Profile banner for ${user.name}` : ''"
            :width="bannerImage?.width"
            :height="bannerImage?.height"
            :show-feature="hasBanner"
            :background-class="bannerBackgroundClass"
          />
        </div>

        <div class="profile-header__identity relative flex flex-col items-center gap-5 px-5 pb-6 pt-16 text-center sm:px-8 sm:pb-7 sm:pt-20 md:flex-row md:items-end md:pt-24 md:text-left xl:pt-28">
          <button
            v-if="isCurrentUser"
            type="button"
            class="profile-header__avatar absolute -top-14 left-1/2 z-10 h-28 w-28 -translate-x-1/2 rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-heroblue-200 sm:-top-16 sm:h-32 sm:w-32 md:-top-20 md:left-8 md:h-40 md:w-40 md:translate-x-0 xl:-top-24 xl:h-48 xl:w-48"
            title="Edit Profile Photo"
            @click="editThumbnail({user})"
          >
            <user-avatar :user="user" size="md" :ui="avatarUi" />
          </button>

          <div v-else class="profile-header__avatar absolute -top-14 left-1/2 z-10 h-28 w-28 -translate-x-1/2 sm:-top-16 sm:h-32 sm:w-32 md:-top-20 md:left-8 md:h-40 md:w-40 md:translate-x-0 xl:-top-24 xl:h-48 xl:w-48">
            <user-avatar :user="user" size="md" :ui="avatarUi" />
            <user-friend-badge v-if="isFriend" class="absolute right-1 top-1 z-10" />
          </div>

          <div class="min-w-0 flex-1">
            <div class="flex min-w-0 items-center justify-center gap-1 md:justify-start">
              <h1 class="min-w-0 break-words font-serif text-3xl leading-tight text-charcoal-600 sm:text-4xl">
                {{ user.name }}
              </h1>
              <u-button
                :icon="COMMON_ICONS.information"
                color="gray"
                variant="ghost"
                title="More Information"
                class="group mt-1 shrink-0 rounded-full"
                :ui="informationButtonUi"
                @click="openDetailsModal({user})"
              />
            </div>
            <p v-if="user.location" class="mt-2 flex items-center justify-center gap-2 text-base text-slate-500 md:justify-start">
              <u-icon name="i-ri-map-pin-line" class="h-5 w-5 shrink-0" />
              <span>{{ user.location }}</span>
            </p>
          </div>

          <user-profile-actions
            :user="user"
            :is-current-user="isCurrentUser"
            :privacy="privacy"
            @update:privacy="$emit('update:privacy', $event)"
          />
        </div>
      </div>
    </div>
  </layout-page-head-section>
</template>

<script setup>
  const props = defineProps({
    user: {
      type: Object,
      required: true
    },
    isCurrentUser: Boolean,
    condensed: Boolean,
    privacy: String
  });

  defineEmits(['update:privacy']);
  const {editThumbnail, openDetailsModal} = useUserActions();

  const bannerImage = computed(() => props.user.banner_images?.large);
  const hasBanner = computed(() => Boolean(bannerImage.value?.url));
  const hasProfilePhoto = computed(() => Boolean(props.user.avatarImage));
  const bannerSource = computed(() => (
    bannerImage.value?.url || props.user.avatarImage || null
  ));
  const bannerBackgroundClass = computed(() => (
    !hasBanner.value && !hasProfilePhoto.value
      ? props.user.profileClassName
      : null
  ));
  const isFriend = computed(() => props.user.friendship?.status === FRIEND_STATUS.completed);
  const avatarUi = {
    wrapper: 'border-4 border-white shadow-xl !h-full !w-full',
    size: {md: '!h-full !w-full text-5xl'}
  };
  const informationButtonUi = {
    icon: {base: 'text-slate-500 transition-colors group-hover:text-slate-700'}
  };

</script>

<style scoped>
  @media (max-height: 900px) {
    .profile-header__banner {
      height: 10rem;
    }
  }

  @media (min-width: 768px) and (max-height: 900px) {
    .profile-header__banner {
      height: 12.5rem;
    }
  }

  @media (min-width: 992px) and (max-height: 900px) {
    .profile-header__identity {
      padding-top: 5rem;
    }

    .profile-header__avatar {
      top: -4.5rem;
      width: 9rem;
      height: 9rem;
    }
  }

  @media (max-height: 700px) {
    .profile-header__banner {
      height: 9rem;
    }
  }
</style>
